import { UnauthorizedError, api } from './api.js';
import { renderLive } from './pages/live.js';
import { renderLogin } from './pages/login.js';
import { renderRecordings } from './pages/recordings.js';
import { renderSettings } from './pages/settings.js';
import { clear, el } from './ui.js';
import './styles.css';

/**
 * App shell: a tiny hash router with a login gate. Any 401 from a page drops
 * the user back to the login screen.
 */

type Route = 'live' | 'recordings' | 'settings';
type Teardown = () => void;

const ROUTES: readonly { id: Route; label: string }[] = [
  { id: 'live', label: 'Live' },
  { id: 'recordings', label: 'Recordings' },
  { id: 'settings', label: 'Settings' },
];

const routeFromHash = (): Route => {
  const raw = window.location.hash.replace(/^#\/?/, '');
  const match = ROUTES.find((route) => route.id === raw);
  return match?.id ?? 'live';
};

const app = document.getElementById('app');
if (app === null) throw new Error('missing #app root element');

let teardown: Teardown | null = null;
/** Set once the shell is mounted; cleared when we fall back to login. */
let shell: { nav: HTMLElement; outlet: HTMLElement } | null = null;

const runTeardown = (): void => {
  if (teardown === null) return;
  try {
    teardown();
  } finally {
    teardown = null;
  }
};

const renderShell = (): { nav: HTMLElement; outlet: HTMLElement } => {
  clear(app);

  const outlet = el('main', { class: 'outlet', id: 'outlet' });
  const nav = el('nav', { class: 'tabbar', role: 'navigation' });

  for (const route of ROUTES) {
    nav.append(el('a', { class: 'tabbar__item', href: `#/${route.id}` }, [route.label]));
  }

  app.append(outlet, nav);
  return { nav, outlet };
};

const showLogin = (): void => {
  runTeardown();
  shell = null;
  clear(app);
  renderLogin(app, () => {
    window.location.hash = '#/live';
    void boot();
  });
};

/** Renders the active route, wiring the shared 401 -> login behaviour. */
const renderRoute = (nav: HTMLElement, outlet: HTMLElement): void => {
  const route = routeFromHash();

  for (const item of nav.querySelectorAll('a')) {
    item.classList.toggle('tabbar__item--active', item.getAttribute('href') === `#/${route}`);
  }

  runTeardown();
  try {
    if (route === 'live') teardown = renderLive(outlet);
    else if (route === 'recordings') teardown = renderRecordings(outlet);
    else teardown = renderSettings(outlet);
  } catch (err) {
    if (err instanceof UnauthorizedError) showLogin();
    else throw err;
  }
};

// Registered exactly once — re-running boot() must not stack listeners.
window.addEventListener('hashchange', () => {
  if (shell !== null) renderRoute(shell.nav, shell.outlet);
});

const boot = async (): Promise<void> => {
  let authenticated = false;
  try {
    authenticated = (await api.session()).authenticated;
  } catch {
    authenticated = false;
  }

  if (!authenticated) {
    shell = null;
    showLogin();
    return;
  }

  shell = renderShell();
  renderRoute(shell.nav, shell.outlet);
};

// A rejected promise from a page poll means the session lapsed mid-use.
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason instanceof UnauthorizedError) {
    event.preventDefault();
    showLogin();
  }
});

void boot();
